import React, { useState, useEffect } from 'react';
import { Table, Tag, Typography, Empty, Button, Modal } from 'antd';
import { EditOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import TiptapEditor from '../components/TiptapEditor/TiptapEditor';

const { Title, Text } = Typography;

const fontFamily = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const stepOrder = [
  { key: 'capture_intent', label: 'Capture Intent' },
  { key: 'generate_brief', label: 'Generate Brief' },
  { key: 'create_toc', label: 'Create TOC' },
  { key: 'index_source', label: 'Index Source' },
  { key: 'generate_doc', label: 'Generate Doc' },
  { key: 'refine_doc', label: 'Refine Doc' },
];

const AIWriterHistory = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data } = await apiClient.get('/notes/aiwriter/history');
        setSessions(data?.sessions || []);
      } catch (err) {
        console.warn('[AIWriterHistory] Failed to fetch history:', err.message);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const columns = [
    {
      title: t('dashboard:aiwriter_history_col_title', { defaultValue: 'Title' }),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (val) => val || '-',
    },
    {
      title: t('dashboard:aiwriter_history_col_status', { defaultValue: 'Status' }),
      dataIndex: 'status',
      key: 'status',
      width: 260,
      render: (status) => {
        if (!status || typeof status !== 'object') return '-';
        const total = stepOrder.length;
        const firstNonCompleteIdx = stepOrder.findIndex(s => status[s.key] !== 'completed');
        if (firstNonCompleteIdx === -1) {
          return <Tag color="green">Completed ({total}/{total})</Tag>;
        }
        const current = stepOrder[firstNonCompleteIdx];
        const state = status[current.key] || 'pending';
        const colorMap = { running: 'blue', failed: 'red', completed: 'green', pending: 'default' };
        return <Tag color={colorMap[state] || 'default'}>{current.label} - {state} ({firstNonCompleteIdx + 1}/{total})</Tag>;
      },
    },
    {
      title: t('dashboard:aiwriter_history_col_created', { defaultValue: 'Created' }),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (val) => val ? new Date(val).toLocaleString() : '-',
    },
    {
      title: t('dashboard:aiwriter_history_col_download', { defaultValue: 'Generated doc' }),
      dataIndex: 'generated_doc',
      key: 'generated_doc',
      width: 240,
      render: (content, record) => {
        const handleDownload = () => {
          const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${record.title || 'document'}.md`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        };
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            <Button
              type="link"
              icon={<EyeOutlined />}
              size="small"
              disabled={!content}
              style={!content ? { color: '#ccc' } : undefined}
              onClick={() => setViewingDoc(record)}
            >
              {t('dashboard:aiwriter_history_view_btn', { defaultValue: 'View' })}
            </Button>
            <Button
              type="link"
              icon={<DownloadOutlined />}
              size="small"
              disabled={!content}
              style={!content ? { color: '#ccc' } : undefined}
              onClick={handleDownload}
            >
              {t('dashboard:aiwriter_history_download_btn', { defaultValue: 'Download' })}
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{
      maxWidth: 1100,
      margin: '0 auto',
      padding: '32px 24px',
      fontFamily,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <EditOutlined style={{ fontSize: 18, color: '#fff' }} />
        </div>
        <div>
          <Title level={4} style={{ margin: 0, fontFamily }}>
            {t('dashboard:aiwriter_history_title', { defaultValue: 'AI Doc Writer Status' })}
          </Title>
          <Text type="secondary" style={{ fontSize: 13, fontFamily }}>
            {t('dashboard:aiwriter_history_subtitle', { defaultValue: 'Document generation status' })}
          </Text>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={sessions}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        locale={{
          emptyText: (
            <Empty
              description={t('dashboard:aiwriter_history_empty', { defaultValue: 'No documents yet' })}
            />
          ),
        }}
        style={{ fontFamily }}
      />

      <Modal
        open={!!viewingDoc}
        onCancel={() => setViewingDoc(null)}
        footer={null}
        width="80%"
        title={viewingDoc?.title || t('dashboard:aiwriter_history_view_title', { defaultValue: 'Generated Document' })}
        destroyOnHidden
      >
        {viewingDoc?.generated_doc && (
          <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
            <TiptapEditor
              value={viewingDoc.generated_doc}
              markdown
              readOnly
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AIWriterHistory;
